// This is just an example,
// so you can safely delete all default props below

export default {
  failed: 'Action failed',
  success: 'Action was successful',
  // Operator settings (`system_settings`). `names` and `groups` are optional:
  // a setting with no translation renders under its server name rather than
  // vanishing.
  settings: {
    title: 'Settings',
    refresh: 'Refresh settings',
    loading: 'Loading settings…',
    loadFailed: 'Could not load the settings. Try again shortly.',
    retry: 'Retry',
    on: 'On',
    off: 'Off',
    empty: 'Nothing is configurable yet.',
    saved: '{name} saved.',
    saveFailed: 'Could not save {name}. The previous value was restored.',
    groups: {
      general: 'General',
      punch_anomalies: 'Punch anomalies',
    },
    // Nested by group, never 'group.key' as one key: vue-i18n resolves the dot
    // as a path, so a literal dotted key never matches and the row falls back
    // to the raw server name.
    names: {
      punch_anomalies: {
        enabled: 'Punch anomaly detection',
      },
      general: {
        first_day_of_week: 'First day of the week',
      },
    },
  },
  punchAnomalies: {
    fab: {
      openSituations: 'open situation | open situations',
      allHandled: 'Every situation is being handled',
      shortLabelLoud: 'Alerts',
      shortLabelCalm: 'Situations',
      shortLabelUnavailable: 'Alerts unavailable',
      // WCAG 2.5.3: the accessible name has to contain the visible label, or
      // speech input cannot activate the button by the word printed on it.
      loudAccessible: 'Alerts: {count} unclaimed situation | Alerts: {count} unclaimed situations',
      unavailable: 'Alerts unavailable — the list could not be loaded. Tap to retry.',
      snoozedByMe: '{count} situation postponed by you, still unclaimed | {count} situations postponed by you, still unclaimed',
      claimedBy: 'Situations in progress, handled by {prenom}',
      claimedByTeam: 'Situations in progress, handled by the team',
    },
    offline: {
      indicatorReconnecting: 'Reconnecting…',
      indicatorOffline: 'Offline',
      bannerActionPending: 'Connection lost — your action will send as soon as the network is back.',
      bannerClaimHeld: 'Connection lost — you still hold this situation, it stays yours.',
      retryShortly: "Can't load right now. Try again shortly.",
    },
    dialog: {
      title: 'Emergency situations',
      claim: "I'll handle it",
      reject: 'Postpone',
      release: 'Step back',
      resolve: 'Resolved',
      resolveComment: 'What happened and what did you do?',
      // Frequent outcomes inserted in one tap into the comment — the text
      // stays editable and the same length bounds apply.
      presetsLabel: 'Preset messages',
      presets: {
        reached: {
          label: 'Reached, on the way',
          text: 'Reached the employee by phone, they are on their way to the site.',
        },
        punchError: {
          label: 'Forgot/punch error',
          text: 'Forgotten or mistaken punch.',
        },
      },
      releaseReason: 'Why are you stepping back?',
      releaseReasonRequired: 'Tell the next coach what you already tried.',
      commentRequired: 'A comment is mandatory.',
      commentTooShort: '{count} characters — minimum {min}.',
      commentTooLong: '{count} characters — maximum {max}.',
      commentCounter: '{count} / {max} characters',
      alreadyClaimed: '{prenom} has been handling it since {time}',
      handover: '{prenom} stepped back at {time}: "{reason}"',
      handoverUnknown: 'Stepped back at {time}: "{reason}"',
      claimedByYou: 'You have been handling it since {time} · {duration}',
      alreadyTaken: 'This situation was just taken by another coach.',
      alreadyTakenBy: 'This situation was just taken by {prenom}.',
      resolvedConfirmation: "{name}'s situation is closed — the comment has gone to HR.",
      snoozed: 'Reminder in {minutes} min',
      snoozedReason: 'Reminder due in {minutes} min.',
      since: 'since {time}',
      call: 'Call',
      sms: 'Text',
      noPhone: 'no number on file',
      empty: 'No open situation.',
      focusAnnounce: '{severity} for {name}: {types}.',
      openedAnnounce: '{count} open situation to handle | {count} open situations to handle',
      actionTimedOut: 'This is taking longer than expected.',
      retry: 'Retry',
      actionFailed: 'Something went wrong. Try again.',
      consequences: {
        claim: 'This situation becomes yours.',
        reject: 'Postpones the reminder by {minutes} min, for you only.',
        // Before the first server response the exact postpone window is
        // unknown: describe the effect without quoting a number.
        rejectUnknownDelay: 'Postpones the reminder, for you only.',
        release: 'You step back — everyone is re-alerted.',
        resolve: 'Closes the situation with your comment.',
      },
      // Folded event of a reopened case + detection time.
      previousEpisode: 'Previous event resolved at {time} · {count} anomaly | Previous event resolved at {time} · {count} anomalies',
      detectedAt: 'detected at {time}',
      // Self-healing: the employee corrected the situation themselves.
      clearedAt: 'Regularized by the employee at {time}',
      // Employee's answer to their own prompt (staged dispatch).
      employeeResponse: {
        pending: 'Employee asked — no answer yet.',
        answered: "Employee's answer: {answer}",
      },
      // Relative durations used by the anomaly fact lines.
      duration: {
        minutes: '{count} min',
        hours: '{count} h',
        hoursMinutes: '{hours} h {minutes}',
      },
      // The sentence the coach triages on, given prominence on the card: the
      // expectation that was missed, not a raw counter. `{elapsed}` is always
      // measured from the missed deadline, never from the start of the work.
      lead: {
        'ANOM-00': 'Should have confirmed {elapsed} ago',
        'ANOM-01': 'Should have arrived {elapsed} ago',
        'ANOM-02': 'Should have punched out {elapsed} ago',
        'ANOM-03': 'Transit unconfirmed',
        'ANOM-04': 'Punch {distance} m from the client',
        'ANOM-05': 'Full in-and-out in {seconds} s',
        'ANOM-06': 'No punch anywhere for {elapsed}',
      },
      // Deadline not actually passed: the delay would read "0 min". Name the
      // state instead of quoting a figure that does not exist.
      leadPending: {
        'ANOM-00': 'Presence unconfirmed',
        'ANOM-01': 'No punch-in',
        'ANOM-02': 'No punch-out',
      },
      // What places the sentence above: the reference times, so the coach has
      // nothing to work out. ANOM-04 and ANOM-05 are a single measurement with
      // nothing to qualify, hence the deliberate absence of a key for those
      // two types.
      context: {
        'ANOM-00': 'Confirmation expected at {time}',
        'ANOM-01': 'Due at {time}',
        'ANOM-02': 'In at {time} · out expected at {dueOut}',
        'ANOM-03': '{reason} · for {elapsed}',
        'ANOM-06': 'Out at {time}',
      },
      // Task with no estimated duration: no expected punch-out to announce.
      contextNoPlan: {
        'ANOM-02': 'In at {time}',
      },
      transitReason: {
        answered_no: 'answered no',
        no_response: 'no answer',
        retries_exhausted: 'retries exhausted',
      },
      staleness: {
        pending: 'Past due — HR has been alerted',
      },
      bulk: {
        enter: 'Multi-select',
        exit: 'Cancel selection',
        selectAll: 'Select all',
        selectCase: 'Select {prenom}',
        resultClaimed: '{name}: taken.',
        resultAlreadyTaken: '{name}: situation already taken by {prenom}.',
        resultRejected: '{name}: postponed.',
        resultNotPermitted: '{name}: action not possible.',
        overCapError: 'Too many situations selected. Narrow your selection and try again.',
        selectedOfTotal: '{selected} selected of {total}',
        selectedCount: 'none selected | {count} selected | {count} selected',
      },
    },
    announce: {
      newAlert: 'New alert for {name}.',
      resolved: '{name}: situation resolved.',
      claimedByOther: '{name}: taken by {prenom}.',
      released: '{name}: available again.',
      claimLost: 'Already taken by {prenom}.',
      multiSelectOn: 'Multi-select turned on.',
      multiSelectOff: 'Multi-select turned off.',
      backOnline: 'Connection restored.',
      connectionLost: 'Connection lost.',
    },
    walkthrough: {
      title: 'Four possible actions',
      dismiss: 'Got it',
      claim: { label: "I'll handle it", consequence: 'This situation becomes yours.' },
      reject: { label: 'Postpone', consequence: 'Postpones the reminder by {minutes} min, for you only.' },
      release: { label: 'Step back', consequence: 'You step back — everyone is re-alerted.' },
      resolve: { label: 'Resolved', consequence: 'Closes the situation with a mandatory comment.' },
    },
    push: {
      explainerTitle: 'Turn on push alerts?',
      explainerBody: 'Notifications are what let you react fast to urgent situations, even outside the app.',
      enable: 'Enable',
      later: 'Later',
      missingWarning: "Notifications are off on this device: you won't receive push alerts.",
      retry: 'Retry',
      dismiss: 'Dismiss this warning',
      settingsTitle: 'Push notifications',
      statusOn: 'Enabled on this device',
      statusOff: 'Disabled on this device',
      statusBlocked: 'Blocked — turn them back on in your browser settings',
      statusUnsupported: 'Not supported by this browser',
      statusFeatureOff: 'Nothing to notify — the punch-anomaly system is turned off',
    },
    severity: {
      alert: 'Alert',
      log: 'Info',
    },
    types: {
      'ANOM-00': 'Presence not confirmed',
      'ANOM-01': 'Missed punch-in',
      'ANOM-02': 'Overdue at a client',
      'ANOM-03': 'Transit check failed',
      'ANOM-04': 'Punch too far from site',
      'ANOM-05': 'Suspicious ghost visit',
      'ANOM-06': 'Abandoned route',
    },
    transit: {
      title: 'Are you on the way?',
      question: 'Are you on the way to {client}?',
      answerBefore: 'Please answer before {time}',
      countdown: '{time} remaining',
      countdownAnnounce: '{minutes} min left to answer.',
      stakes:
        'No answer counts as a failed check. You may be asked once more; on that final check-in, any answer closes the check.',
      finalNotice: 'Last check-in — any answer closes the check, no further prompt.',
      yes: "Yes, I'm on my way",
      yesFinal: 'Yes, close the check',
      no: 'No',
      sending: 'Sending…',
      queued: 'Waiting for connection — retrying automatically…',
      sent: 'Sent.',
      ackNo: 'Noted — a coach will contact you.',
      ackYesExtended: "Thanks — you'll be asked again shortly.",
      ackYesClosed: 'Thanks, your answer was recorded.',
      lateAnswer: 'This check already timed out — your answer arrived too late.',
      expiredAnnounce: 'Check expired, no answer received.',
    },
    // Asks the employee to explain before coaches are alerted (staged
    // dispatch, ANOM-01/02/06).
    employeePrompt: {
      title: 'Everything okay?',
      question: {
        'ANOM-00': 'You have not confirmed your presence. Will you be at {client}?',
        'ANOM-01': 'You still have not punched in at {client}.',
        'ANOM-02': 'You still have not punched out at {client}.',
        'ANOM-06': 'You have not punched in at {client} since leaving.',
        default: 'Your punch looks unusual.',
      },
      answerBefore: 'Please answer before {time}, after which a coach will be notified.',
      // One notification can cover several questions; the dialog only shows
      // the oldest, so it says what is still behind it.
      queueRemaining: '{count} more question after this one | {count} more questions after this one',
      notePlaceholder: 'Detail (optional)',
      // Labels by server key; `forgot_punch` reads differently depending on
      // whether the missing punch is an entry or an exit.
      presets: {
        // Neutral fallback: the HR timeline can attach an event to an
        // anomaly whose type is unknown (`anomaly_id` deleted or
        // unresolved), whereas CaseCard.vue always has a concrete
        // anomaly.type.
        forgot_punch: 'Forgot to punch',
        on_my_way: 'I am on my way',
        will_be_present: 'I will be present',
        still_working: 'I am still working, it is taking longer',
        forgot_punch_in: 'I forgot to punch in, I am on site',
        forgot_punch_out: 'I forgot to punch out',
        need_help: 'I have a problem, I need help',
      },
      sending: 'Sending…',
      ackSent: 'Thanks, your answer has been sent.',
      sendFailed: 'Sending failed. Try again.',
    },
    rh: {
      title: 'Punch anomalies',
      cases: 'Situations',
      unresolved: 'Unresolved',
      avgClaim: 'Avg. time to claim',
      avgResolve: 'Avg. time to resolve',
      employee: 'Employee',
      anomalies: 'Anomalies',
      client: 'Client',
      detectedAt: 'Detected at',
      claimedBy: 'Claimed',
      resolvedBy: 'Resolved',
      comment: 'Comment',
      timeline: 'Full timeline',
      noCases: 'No situation on that day.',
      escalated: 'Escalated to HR',
      cellulaire: 'mobile',
      telephoneFixe: 'landline',
      factPromptedAt: 'check requested at {time}',
      viewOnMap: 'View the punch on the map',
      clearedInline: 'regularized',
      claimStaleSince: 'Claim inactive since {time}',
      exports: {
        title: "Today's exports",
      },
      events: {
        anomaly_detected: 'Anomaly detected',
        reopened: 'Case reopened (new alert after resolution)',
        employee_prompted: 'Employee asked before alerting coaches',
        employee_responded: "Employee's answer",
        anomaly_cleared: 'Anomaly regularized by the employee',
        auto_resolved: 'Case auto-resolved (regularized)',
        notified: 'Notification sent',
        // The websocket keeping coach screens current — never an alert. Said
        // outright: this row also appears during the employee response grace,
        // before any coach has been alerted.
        notified_sync: 'Screens updated for {names} (no alert)',
        claim: 'Claimed',
        reject: 'Postponed',
        reject_snooze: 'Postponed ({minutes} min)',
        release: 'Released',
        force_release: 'Force-released by HR',
        resolve: 'Resolved',
        snooze_expired: 'Postponement expired',
        escalated_rh_email: 'HR email sent',
        escalation_skipped: 'Escalation skipped (no recipient)',
        escalation_failed: 'Escalation email failed permanently',
        duplicate_suppressed: 'Repeat suppressed',
        gps_missing: 'GPS missing at punch',
        entry_time_missing: 'No entry time resolvable — no-show rule could not run',
        recipients_extended: 'Coaches added to the responsible list',
        stale_released: 'Inactive claim released by the system',
        export_performed:
          'CSV export generated by {actor}: {from} → {to} ({count} row) | CSV export generated by {actor}: {from} → {to} ({count} rows)',
      },
      detected: {
        'ANOM-00': 'Presence unconfirmed at {client}',
        'ANOM-01': 'Entry not punched at {client}',
        'ANOM-02': 'Exit not punched at {client}',
        'ANOM-03': 'Transit check failed toward {client}',
        'ANOM-04': 'Punch outside the zone at {client}',
        'ANOM-05': 'Suspiciously short visit at {client}',
        'ANOM-06': 'Route abandoned en route to {client}',
      },
      // Same fact without a client, for administrative anomalies with no site.
      detectedBare: {
        'ANOM-00': 'Presence unconfirmed',
        'ANOM-01': 'Entry not punched',
        'ANOM-02': 'Exit not punched',
        'ANOM-03': 'Transit check failed',
        'ANOM-04': 'Punch outside the zone',
        'ANOM-05': 'Suspiciously short visit',
        'ANOM-06': 'Route abandoned',
      },
      detectedChronology: {
        'ANOM-00': 'Presence not confirmed for tonight ({lag} late)',
        'ANOM-01': '{client}: Entry not punched ({lag} late)',
        'ANOM-02': '{client} — On site for {onSite}: Exit not punched',
        'ANOM-02LastStop': '{client}: Did not punch their final exit',
        'ANOM-03': '{from} → {to}: Not there yet ({reason})',
        'ANOM-03Bare': 'Not there yet ({reason})',
        'ANOM-04': '{client}: Punch outside the zone ({distance} m)',
        'ANOM-05': '{client}: Brief visit ({seconds} s)',
        'ANOM-06': '{client} — No punch since {sinceOut}: Route abandoned',
        'ANOM-01Bare': 'Entry not punched ({lag} late)',
        'ANOM-02Bare': 'On site for {onSite}: Exit not punched',
        'ANOM-02LastStopBare': 'Did not punch their final exit',
        'ANOM-04Bare': 'Punch outside the zone ({distance} m)',
        'ANOM-05Bare': 'Brief visit ({seconds} s)',
        'ANOM-06Bare': 'No punch since {sinceOut}: Route abandoned',
      },
      transitReasons: {
        noResponse: 'reminder went unanswered',
        answeredNo: 'said they were not going',
        retriesExhausted: 'still not there after follow-up',
      },
      detectionSupport: {
        workVisit: 'In at {start} · estimated exit at {time}',
      },
      channels: {
        push: 'push notification',
        in_app: 'in-app',
        email: 'email',
      },
      export: {
        button: 'Export (CSV)',
        title: 'Export anomalies',
        from: 'From',
        to: 'To',
        rangeNote: 'Maximum range: {max} days',
        rangeExceeded: 'Range shortened to {max} days (allowed maximum).',
        submit: 'Download',
        cancel: 'Cancel',
        error: 'The export failed. Try again or narrow the date range.',
      },
      warnings: {
        scan_stale: "The anomaly scan hasn't run in over 15 minutes — new situations may be slow to appear. Contact technical support if this persists.",
        routes_budget_exhausted: 'The daily transit-check limit has been reached — checks resume tomorrow. No action needed on your part.',
        detection_off: 'Anomaly detection is turned off — no new situation will be detected until it is re-enabled. Contact an administrator.',
        dispatch_muted: 'Coach alerts are muted — coaches no longer receive notifications, but HR escalation emails still go out normally. Contact an administrator to turn alerts back on.',
        escalation_list_broken: "The escalation recipient list is empty or matches no active HR account — escalation emails may not reach anyone. Contact an administrator to fix the list.",
      },
      notEvaluated: 'Not evaluated: anomaly detection was not yet enabled that day (enabled since {date}).',
      notEvaluatedNever: 'Anomaly detection has never been enabled — no day has been evaluated yet.',
      featureDisabled: 'The punch-anomaly system is turned off: nothing is being detected and no situation is being recorded, so there is nothing to show here until it is turned back on.',
      featureDisabledAction: 'Open system settings',
      timelineLoadMore: 'Show more ({shown} of {total})',
      toRecipient: 'to',
      // A grouped send names its recipients while they stay readable; past
      // that the overflow becomes a count — the full list is already at the
      // top of the case.
      recipientOthers: '{count} other | {count} others',
      technicalDetail: {
        show: 'Technical detail ({count} row) | Technical detail ({count} rows)',
        hide: 'Hide technical detail',
      },
      // The "by event" view: the same day re-read in the order it happened,
      // rather than grouped by employee.
      table: {
        viewList: 'By situation',
        viewTable: 'By event',
        noRows: 'No event matches these filters.',
        openHint: 'Click a row to open its situation.',
        rowCount: '{shown} of {total} events',
        footnote: 'Every event on the day shown, situations and exports alike. For a date range, use the CSV export.',
        columns: {
          time: 'Time',
          employee: 'Employee',
          event: 'Event',
          anomaly: 'Anomaly',
          people: 'Actor / recipients',
          channel: 'Channel',
        },
        filters: {
          employee: 'Employee',
          eventType: 'Event type',
          severity: 'Severity',
          severityCount: '{count} severity | {count} severities',
          channel: 'Channel',
          search: 'Search',
          technical: 'Technical detail',
          typeExport: 'CSV export',
          typeCount: '{count} type | {count} types',
        },
      },
      // Third severity state, between "Alert" and "Info": an alert still
      // inside the employee response grace. Nobody has been alerted, but it
      // turns loud on its own if the employee stays silent.
      awaitingEmployee: 'Awaiting employee',
      // The employee answered something reassuring, but the anomaly still
      // stands: the answer cancels neither the alert nor the grace. HR has to
      // see "they said they were on their way and the punch never landed".
      answeredNotCleared: 'Answered, not regularized',
      unclaimed: 'Unclaimed',
      previousDay: 'Previous day',
      nextDay: 'Next day',
      dateField: 'Date (YYYY-MM-DD)',
      openCalendar: 'Open the calendar',
      actions: {
        forceRelease: 'Release coach (HR)',
        forceReleaseConsequence: 'Releases the currently assigned coach — everyone is re-alerted.',
        forceReleaseDone: '{name}: coach released by HR.',
        resolve: 'Resolve (HR)',
        resolveDone: '{name}: situation resolved by HR.',
      },
      walkthrough: {
        title: 'HR overview',
        dismiss: 'Got it',
        warnings: {
          label: 'Status banners',
          consequence: 'Flag any operational issue: scan stalled, detection off, daily limit reached, and so on.',
        },
        cases: {
          label: "Today's situations",
          consequence: "Expand a row to see that situation's full log.",
        },
        overrides: {
          label: 'HR interventions',
          consequence: 'Release a coach or resolve a situation directly — a comment is mandatory to resolve.',
        },
        export: {
          label: 'CSV export',
          consequence: 'Download anomalies over a date range from the download button.',
        },
      },
    },
  },
  // `fr/index.ts` carries the full `presences` block (this app's primary
  // locale); English only ever had `punchAnomalies` translated here, so
  // this key stays scoped to the one string Task 19 added rather than
  // backfilling the rest of an already-partial locale file.
  presences: {
    anomalyIndicator: 'Anomaly flagged — {severity}: {types}. Open in HR view.',
  },
  coachContact: {
    fab: {
      label: 'A problem?',
      aria: 'A problem? Reach a coach on duty',
    },
    dialog: {
      title: 'Reach a coach',
      intro: 'Coaches scheduled today.',
      call: 'Call {name}',
      sms: 'Text {name}',
      noPhone: 'no number on file',
      loading: 'Loading coaches…',
      empty: 'Nobody to reach right now.',
      error: 'The coach list could not be loaded.',
      stale: 'List not refreshed — showing the last one received.',
      retry: 'Retry',
    },
  },
};
